import manifest from "../../../../config/questionnaire-text-limits.json";

/**
 * The text limits of a questionnaire draft, owned by
 * `config/questionnaire-text-limits.json`. Python reads the same file
 * (`apps/python-api/models/questionnaire/text_limits.py`). Every TypeScript
 * reader of these limits derives from here.
 */
const KEYS = [
  "studyObjectiveMaxChars",
  "studyObjectiveMaxBullets",
  "assetNameMaxChars",
  "assetDescriptionMaxWords",
  "questionLabelMaxChars",
  "videoSceneMaxShots",
  "videoSceneShotMaxChars",
  "coverageRationaleMaxChars",
  "designBriefMethodMaxChars",
  "designBriefLineMaxChars",
  "studyNameMaxChars",
] as const;

if (
  manifest.schemaVersion !== "questionnaire-text-limits.v1" ||
  manifest.lengthUnit !== "codePoints" ||
  Object.keys(manifest).length !== KEYS.length + 2 ||
  KEYS.some((key) => !Number.isInteger(manifest[key]) || manifest[key] < 1)
) {
  throw new TypeError("Questionnaire text-limit registry is malformed.");
}

/** One budget for the whole study objective: the decision and its learning objectives. */
export const STUDY_OBJECTIVE_MAX_CHARS: number = manifest.studyObjectiveMaxChars;

/** The most learning objectives an objective lists: more is a list, not a study. */
export const STUDY_OBJECTIVE_MAX_BULLETS: number =
  manifest.studyObjectiveMaxBullets;

/**
 * A tested asset's stored display name. Claim (`message`) titles stay
 * unbounded because the Title field is the stimulus.
 */
export const DEFAULT_MAX_ASSET_NAME_LENGTH: number = manifest.assetNameMaxChars;

/**
 * A tested asset's description, and the shared asset context, in words: a
 * soft ceiling legitimate studies never approach.
 */
export const MAX_ASSET_DESCRIPTION_WORDS: number =
  manifest.assetDescriptionMaxWords;

/**
 * An option, row, category or measure label: the deck's own `maxLength` for
 * these fields (`packages/slide-schemas/presets/*.schema.json`), which a test
 * pins, so the builder refuses what the deck cannot render.
 */
export const MAX_QUESTION_LABEL_LENGTH: number = manifest.questionLabelMaxChars;

/** A video's upload-time scene manifest: at most this many shots. */
export const VIDEO_SCENE_MAX_SHOTS: number = manifest.videoSceneMaxShots;

/** One scene-manifest shot's description, in characters. */
export const VIDEO_SCENE_SHOT_MAX_CHARS: number = manifest.videoSceneShotMaxChars;

/** A topic-coverage result's rationale, in characters. */
export const COVERAGE_RATIONALE_MAX_CHARS: number = manifest.coverageRationaleMaxChars;

/** A design brief's method, in characters. */
export const DESIGN_BRIEF_METHOD_MAX_CHARS: number = manifest.designBriefMethodMaxChars;

/** A design brief's one-line field (its primary metric, its named action), in characters. */
export const DESIGN_BRIEF_LINE_MAX_CHARS: number = manifest.designBriefLineMaxChars;

/** The study's name (`designBrief.authoredObjective.title`), in characters. */
export const STUDY_NAME_MAX_CHARS: number = manifest.studyNameMaxChars;

/**
 * A text's length in the registry's unit (`lengthUnit: codePoints`): Unicode
 * code points, as Python's `len` and JSON Schema's `maxLength` count, never
 * UTF-16 units (`String.length` counts an emoji twice). Every character limit
 * above is counted by this.
 */
export function textLength(value: string): number {
  return Array.from(value).length;
}
